import clsx from "classnames";
import { useState, useCallback, useMemo } from "react";
import { useStyles } from "./Directory.style";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { DirectoryTab } from "../../../constants/services";
import {
  setIsShowService,
  triggerDelayWithTimeout,
} from "../../../store/slices/popup";
import AppBar from "../AppBar/AppBar";
import { Icon } from "../../core/Icon/Icon";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { TabsList } from "../TabsList";
import { DirectoryRow } from "./DirectoryRow";
import { useDirectoryData } from "../../../hooks/useDirectoryData";
import { useTranslation } from "react-i18next";

export type ImageSet = { src1x: string; src2x?: string; src3x?: string };

const Directory: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const directoryItems = useAppSelector(
    (state) => state.theme.theme.pages?.directory || [],
  );
  const LIST_TAB_DIRECTORY = directoryItems
    .map((item) => DirectoryTab[item]);
  const { t } = useTranslation();
  const { favouriteGames, recentlyPlayed, listService, subscriptions, allGames } = useDirectoryData();
  const [activeTab, setActiveTab] = useState(LIST_TAB_DIRECTORY[0]);
  const { isShowService, isPopupDelayActive } = useAppSelector(
    (state) => state.popup,
  );

  const onCloseServicePopup = useCallback(() => {
    dispatch(triggerDelayWithTimeout(250));
    dispatch(setIsShowService({ isShowService: false }));
  }, [dispatch]);

  const onChangeTab = useCallback((tab: string) => {
    setActiveTab(tab);
  }, []);

  const sections = useMemo(
    () => [
      {
        id: "allGames",
        title: DirectoryTab.allGames,
        data: allGames,
      },
      {
        id: "mySubscriptions",
        title: DirectoryTab.mySubscriptions,
        data: subscriptions,
      },
      {
        id: "myFavorites",
        title: DirectoryTab.myFavorites,
        data: favouriteGames,
      },
      {
        id: "recentlyPlayed",
        title: DirectoryTab.recentlyPlayed,
        data: recentlyPlayed,
      },
      {
        id: "gameServices",
        title: DirectoryTab.gameServices,
        data: listService,
      },
    ].filter((section) => directoryItems.includes(section.id)),
    [subscriptions, favouriteGames, recentlyPlayed, listService],
  );

  const renderTabContent = (activeTab: string) => {
    if (activeTab === DirectoryTab.allServices) {
      const visibleSections = sections.filter(
        (section) => section.data.length > 0,
      );
      return (
        <div className={classes.rowAllTab}>
          {
            visibleSections.map((section, index) => (
              <div key={section.id}>
                <DirectoryRow
                  items={section.data}
                  title={section.title}
                  id={DirectoryTab.allServices} />
                {index < visibleSections.length - 1 && (
                  <div className={classes.divider} />
                )}
              </div>
            ))
          }
        </div>
      );
    }

    const currentSection = sections.find((s) => s.title === activeTab);
    return (
      <DirectoryRow
        items={currentSection?.data || []}
        title={currentSection?.title || ""}
        id={currentSection?.id || ""}
        className={classes.rowAllTab}
      />
    );
  };

  if (!isShowService && !isPopupDelayActive) return null;
  return (
    <div
      className={clsx(
        classes.root,
        isShowService ? classes.forwardsAnimate : classes.backwardsAnimate,
      )}
    >
      <AppBar
        leftItems={
          <Icon
            className={classes.icon}
            name="icon-linear-arrow-left-3"
            onClick={onCloseServicePopup}
            data-testid="settings-icon"
          />
        }
        centerItems={
          <Typography variant={TypographyVariant.BodyB4Medium}>
            {t("Directory")}
          </Typography>
        }
      />
      <div className={classes.service}>
        <TabsList
          activeTab={activeTab}
          onChangeTab={onChangeTab}
          listTabs={LIST_TAB_DIRECTORY}
          className={classes.tabs}
        />
        {renderTabContent(activeTab)}
      </div>
    </div>
  );
};

export default Directory;
